import { useMemo } from 'react';
import { useDraft } from '../../state/draft';
import { documentReady } from '@/onboarding/pickContent';
import { useThumbnails } from './thumbnails';

/**
 * What goes into the app besides the plan, on the review — deep-dive F2 and F11.
 *
 * The review listed features and nothing else, so photos from a previous app could ride into this
 * one with no sign but a list of names on a step nobody reopened. Now the photos are shown, with
 * the line the build will use for each, and every document is named with what will happen to it —
 * including a long text that will NOT be built, for want of saying where it came from.
 *
 * Nothing added, nothing drawn.
 */
export default function AttachmentsReview() {
  const media = useDraft((s) => s.attachedMedia);
  const docs = useDraft((s) => s.attachedDocs);
  const attachedDoc = useDraft((s) => s.attachedDoc);
  const book = useDraft((s) => s.pendingBook);

  const photos = useMemo(() => media.filter((m) => m.contentType === 'image/jpeg'), [media]);
  const clips = media.filter((m) => m.contentType !== 'image/jpeg');
  const thumbs = useThumbnails(photos);
  // The step keeps the book in progress; after a trip to Stripe only the finished one is back.
  const reader = book
    ? { name: book.fileName, attached: documentReady(book) }
    : attachedDoc
      ? { name: attachedDoc.fileName ?? attachedDoc.title, attached: true }
      : null;

  if (!media.length && !docs.length && !reader) return null;

  return (
    <section aria-label="What you added">
      <h3 className="type-micro section-label">What you added</h3>
      {photos.length > 0 && (
        <ul className="review-photos">
          {photos.map((m) => (
            <li key={m.name} className="review-photo">
              {thumbs[m.name] ? (
                <img className="content-thumb" src={thumbs[m.name]} alt="" />
              ) : (
                <span className="content-thumb" aria-hidden="true" />
              )}
              {/* The line the build is given; with none, the name is all it has. */}
              <span className="type-body">{m.description?.trim() || m.name}</span>
            </li>
          ))}
        </ul>
      )}
      {(clips.length > 0 || docs.length > 0 || reader) && (
        <ul className="plan-review-list">
          {clips.map((m) => (
            <li key={m.name} className="type-body">{m.name}</li>
          ))}
          {docs.map((d) => (
            <li key={d.name} className="type-body">
              {d.name} <span className="type-caption plan-detail">— written into the page</span>
            </li>
          ))}
          {reader && (
            <li className="type-body">
              {reader.name}{' '}
              <span className={reader.attached ? 'type-caption plan-detail' : 'type-caption field-error-text'}>
                {reader.attached
                  ? '— kept for people to read'
                  : '— not attached: it needs saying where it came from, on “Your own content”'}
              </span>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}
